import { MainContext } from "components/utils/Contexts";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { updateRecordRequest, createRecordRequest } from "services/apiRequests";
import {
  BrandListType,
  ContactListType,
  LocationListType,
} from "utils/interfaces";
import { AnimatePresence } from "framer-motion";
import {
  MdAdd,
  MdAddCircleOutline,
  MdMenuBook,
  MdOutlineGroupAdd,
} from "react-icons/md";
import InputHeaderImage from "../placeholders/InputHeaderImage";
import TextInput from "../components/create-edit-form/TextInput";
import BinaryRadioButtons from "../components/create-edit-form/BinaryRadioButtons";
import TextArea from "../components/create-edit-form/TextArea";
import AddContact from "../components/create-edit-form/AddContact";
import AddLocation from "components/main-view/utils/components/create-edit-form/AddLocation";
import AddLocationPanel from "../pop-up-panels/add-location-pop-up";
import AnimatedToggle from "components/utils/ToggleButton";
import CheckboxItem from "../components/create-edit-form/CheckboxItem";
import InputBadgeImage from "../placeholders/InputBadgeImage";
import AddListButton from "../components/create-edit-form/AddListButton";
import SelectLessonCategoriesPanel from "../pop-up-panels/select-lesson-categories-pop-up";
import CategoryLessonPill from "./CategoryLessonPill";
import AddAIPromptPanel from "../pop-up-panels/add-ai-prompt-pop-up";
import AIPromptPill from "./AIPromptPill";

interface AddNewBrandProps {
  ref?: any;
  selectedData?: BrandListType | null;
  afterSaveFunction: () => Promise<void>;
  headerRef: React.MutableRefObject<any>;
}

// Parameter definition
const AI_PROMPTS_LIMIT = 5;
const customerTypeOptions = ["B2B", "B2C"];
const classesTerminologyOptions = ["Classes", "Cohorts"];
const studentsTerminologyOptions = ["Students", "Trainees"];
const schoolTerminologyOptions = ["Schools", "Organisations"];
const dataRequirementsFields = [
  { text: "First Name", value: "firstName" },
  { text: "Middle Name (Optional)", value: "middleName" },
  { text: "Last Name", value: "lastName" },
  { text: "Email", value: "email" },
  { text: "Username", value: "username" },
  { text: "Date of Birth", value: "dob" },
  { text: "Gender", value: "gender" },
  { text: "Class/Cohort Name", value: "className" },
  { text: "Parent/Guardian Details", value: "parentDetails" },
  {
    text: "Aboriginal or Torres Straight Islander Origin",
    value: "heritage",
  },
  { text: "Notes", value: "notes" },
];

interface S3ImagesType {
  logo: string | null;
}

const AddNewBrand: React.FC<AddNewBrandProps> = forwardRef(
  ({ selectedData, afterSaveFunction, headerRef }, ref) => {
    // States definition
    const [s3Images, setS3Images] = useState({
      logo: null,
      bgImageLearningJourney: null,
      iconKnowledgePoints: null,
      bgImageKnowledgePoints: null,
      bgImageMyBadges: null,
      bgAIAssistantImage1: null,
      bgAIAssistantImage2: null,
    });
    const [logo, setLogo] = useState<string | null>(null);
    const [brandName, setBrandName] = useState("");
    // const [brandDomainName, setBrandDomainName] = useState("");
    // const [platformName, setPlatformName] = useState("");
    const [tenantName, setTenantName] = useState("");
    const [supportEmail, setSupportEmail] = useState("");
    const [noReplyEmail, setNoReplyEmail] = useState("");
    const [noReplyEmailHost, setNoReplyEmailHost] = useState("");
    const [noReplyEmailPwd, setNoReplyEmailPwd] = useState("");
    const [licensesEmail, setLicensesEmail] = useState("");
    const [customerType, setCustomerType] = useState(customerTypeOptions[0]);
    const [brandDescription, setBrandDescription] = useState("");
    const [feedbackWidget, setFeedbackWidget] = useState("");
    const [selectedContacts, setSelectedContacts] = useState<ContactListType[]>(
      []
    );
    const [locations, setLocations] = useState<LocationListType[]>([]);
    const [lessonCategories, setLessonCategories] = useState<string[]>([]);
    const [classTerminology, setClassTerminology] = useState(
      classesTerminologyOptions[0]
    );
    const [studentsTerminology, setStudentsTerminology] = useState(
      studentsTerminologyOptions[0]
    );
    const [schoolTerminology, setSchoolTerminology] = useState(
      schoolTerminologyOptions[0]
    );
    const [dataRequirements, setDataRequirements] = useState<string[]>([]);
    const [loginScreen, setLoginScreen] = useState<string | null>(null);
    const [deployed, setDeployed] = useState(false);
    // AI assistant states
    const [AIAvatar, setAIAvatar] = useState<string | null>(null);
    const [AIAssistantName, setAIAssistantName] = useState("");
    const [AIAssistantIntro, setAIAssistantIntro] = useState("");
    const [AIAssistantGreeting, setAIAssistantGreeting] = useState("");
    const [AIPrompts, setAIPrompts] = useState<
      { text: string; primary: boolean }[]
    >([]);

    const [AIAvatar2, setAIAvatar2] = useState<string | null>(null);
    const [AIAssistantName2, setAIAssistantName2] = useState("");
    const [AIAssistantIntro2, setAIAssistantIntro2] = useState("");
    const [AIAssistantGreeting2, setAIAssistantGreeting2] = useState("");
    const [AIPrompts2, setAIPrompts2] = useState<
      { text: string; primary: boolean }[]
    >([]);
    // Colour states
    const [secondary50, setSecondary50] = useState<string>("");
    const [secondary100, setSecondary100] = useState<string>("");
    const [secondary200, setSecondary200] = useState<string>("");
    const [secondary300, setSecondary300] = useState<string>("#000000");
    const [secondary400, setSecondary400] = useState<string>("#000000");
    const [secondary500, setSecondary500] = useState<string>("");
    const [secondary600, setSecondary600] = useState<string>("");
    const [secondary700, setSecondary700] = useState<string>("");
    const [secondary800, setSecondary800] = useState<string>("#000000");
    const [secondary900, setSecondary900] = useState<string>("#000000");
    // Dashboard custom imagery
    const [bgImageLearningJourney, setBgImageLearningJourney] = useState<
      string | null
    >(null);
    const [iconKnowledgePoints, setIconKnowledgePoints] = useState<
      string | null
    >(null);
    const [bgImageKnowledgePoints, setBgImageKnowledgePoints] = useState<
      string | null
    >(null);
    const [bgImageMyBadges, setBgImageMyBadges] = useState<string | null>(null);
    // AI assistant custom imagery
    const [bgAIAssistantImage1, setBgAIAssistantImage1] = useState<
      string | null
    >(null);
    const [bgAIAssistantImage2, setBgAIAssistantImage2] = useState<
      string | null
    >(null);
    // Enable states
    const [enableAIAssistant, setEnableAIAssistant] = useState(false);
    const [enableAIAssistant2, setEnableAIAssistant2] = useState(false);
    const [addCustomDashboardImagery, setAddCustomDashboardImagery] =
      useState(false);
    const [addCustomAIAssistantImagery, setAddCustomAIAssistantImagery] =
      useState(false);
    // Pop ups states
    const [showAddLocationPopUp, setShowAddLocationPopUp] = useState(false);
    const [showAddLessonsPopUp, setShowAddLessonsPopUp] = useState(false);
    const [showEditContact, setShowEditContact] = useState(false);
    const [showAddAIPromptPopUp, setShowAddAIPromptPopUp] = useState(false);
    const [showAddAIPromptPopUp2, setShowAddAIPromptPopUp2] = useState(false);

    // State for edit contact
    const [selectedDataContact, setSelectedDataContact] =
      useState<ContactListType | null>(null);

    // Boolean that defines the logic for activating the save button
    const activateSaveButton =
      !!brandName &&
      !!tenantName &&
      !!supportEmail &&
      !!noReplyEmail &&
      !!noReplyEmailHost &&
      !!noReplyEmailPwd &&
      !!licensesEmail &&
      !!customerType &&
      !!brandDescription &&
      lessonCategories.length > 0 &&
      dataRequirements.length > 0 &&
      (!enableAIAssistant ||
        (!!AIAssistantName &&
          !!AIAssistantIntro &&
          !!AIAssistantGreeting &&
          !!AIAvatar)) &&
      (!enableAIAssistant2 ||
        (!!AIAssistantName2 &&
          !!AIAssistantIntro2 &&
          !!AIAssistantGreeting2 &&
          !!AIAvatar2)) &&
      !!secondary50 &&
      !!secondary100 &&
      !!secondary200 &&
      // !!secondary300 &&
      // !!secondary400 &&
      !!secondary500 &&
      !!secondary600 &&
      !!secondary700;
    // !!secondary800 &&
    // !!secondary900;

    // Get the context parameters
    const { setShowSideBarMenu } = useContext(MainContext);

    /**
     * Functions to externalise using refs
     */
    useImperativeHandle(ref, () => ({
      handleSaveSchool() {
        handleSave();
      },
    }));

    /**
     * Root definition
     */
    useEffect(() => {
      defaultView();
    }, []);

    /**
     * Function that makes the root definition
     */
    const defaultView = () => {
      // Hide the side bar menu
      setShowSideBarMenu(false);

      // (Main Header) Set titles and the back button
      headerRef.current.setShowBackButton(true);

      // (Dashboard Header) Set the button to display
      headerRef.current.options().setShowCreateNewPopUp(false);
      headerRef.current.options().setShowCreateNewButton(false);
      headerRef.current.options().setSaveButton({ show: true, disabled: true });
    };

    /**
     * Function to handle save pressing
     */
    const handleSave = async () => {
      // First of all, check if the save button should be working
      if (!activateSaveButton) return;

      // Get the data from the settings form
      const data = provideData();

      // Request for create/edit new school
      let response = { successful: false, message: "", _id: "" };
      if (selectedData) {
        response = await updateRecordRequest(
          { _id: selectedData._id, toUpdate: data },
          "/api/brands"
        );
      } else {
        // Create a new school
        response = await createRecordRequest(data, "/api/brands");
      }

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // And refresh the data
      await afterSaveFunction();
    };

    /**
     * Function that returns all the form data
     * @returns data
     */
    const provideData = () => {
      // Get just the ids from the classes
      const contacts = selectedContacts.map(
        (item: ContactListType) => item._id
      );

      // Create assistants array based on enabled status
      const assistants = [];
      if (enableAIAssistant) {
        assistants.push({
          name: AIAssistantName,
          intro: AIAssistantIntro,
          greeting: AIAssistantGreeting,
        });
        if (enableAIAssistant2) {
          assistants.push({
            name: AIAssistantName2,
            intro: AIAssistantIntro2,
            greeting: AIAssistantGreeting2,
          });
        }
      }

      // Return all the gathered data in the form
      const dataToProvide = {
        backendDB: {
          tenant: tenantName,
          senderEmail: noReplyEmail,
          emailHost: noReplyEmailHost,
          senderPwd: noReplyEmailPwd,
          helpCenter: supportEmail,
          contacts,
          locations,
          lessonCategories,
          dataRequirements,
        },
        frontendDB: {
          colors: {
            secondary: {
              50: secondary50,
              100: secondary100,
              200: secondary200,
              300: secondary300,
              400: secondary400,
              500: secondary500,
              600: secondary600,
              700: secondary700,
              800: secondary800,
              900: secondary900,
            },
          },
          title: brandName,
          tenant: tenantName,
          // platform name automatically determined in backend
          licensesEmail,
          helpCenter: supportEmail,
          customerType,
          blurb: brandDescription,
          feedbucketKey: feedbackWidget, // Temporarily just using feedbucket keys, not entirely dynamic scripts
          locations,
          classesTerminology: classTerminology,
          studentsTerminology,
          schoolTerminology,
          lessonCategories,
          dataRequirements,
          assistants, // This will now be an array with only enabled assistants
        },
        images: {
          logo,
          learningJourneyWatermark: bgImageLearningJourney,
          knowledgeBadge: iconKnowledgePoints,
          knowledgeWatermark: bgImageKnowledgePoints,
          badgesWatermark: bgImageMyBadges,
          assistantWatermark1: bgAIAssistantImage1,
          assistantWatermark2: bgAIAssistantImage2,
          assistantImage1: AIAvatar,
          assistantImage2: AIAvatar2,
          loginBackground: loginScreen,
          // favicon?
          // quizWatermark1?
          // quizWatermark2?
        },
      };

      // Formatting the data to be sent as a request
      // const formattedData = formatDataSchool(selectedData, dataToProvide);
      const formattedData = dataToProvide;

      return formattedData;
    };

    /**
     * Function executed when clicking one of the options
     * @param option
     * @returns
     */
    const handleClickDataRequirementOption = (option: {
      text: string;
      value: string;
    }) => {
      return () => {
        // If the element is selected, then remove
        if (dataRequirements.includes(option.value)) {
          setDataRequirements((currentList) =>
            currentList.filter((item) => option.value !== item)
          );
        }
        // Otherwise,add to the list
        else {
          setDataRequirements((currentList) => [...currentList, option.value]);
        }
      };
    };

    return (
      <div className="w-full h-full flex justify-center overflow-y-auto custom-scroll">
        <div className="w-[562px] h-full p-6 rounded-3xl justify-start items-start gap-20 inline-flex flex-col">
          <div className="w-full flex-col justify-start items-start gap-6 inline-flex">
            <div className="self-stretch h-fit flex-col justify-start items-start gap-12 flex">
              <div className="self-stretch text-neutral-700 text-[23px] font-semibold font-sans leading-7 select-none">
                Basic details
              </div>
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
              <InputHeaderImage
                text="Upload brand logo"
                headerImage={logo}
                setHeaderImage={setLogo}
              />
              <TextInput
                title="Brand Name (User Facing)*"
                placeholder="Enter Brand name"
                state={brandName}
                setState={setBrandName}
              />
              {/* <TextInput
                title="Brand Domain (User Facing)*"
                placeholder="e.g., https://www.edtechgroup.com.au"
                state={brandDomainName}
                setState={setBrandDomainName}
              />
              <TextInput
                title="Platform Name (Non-User Facing)*"
                placeholder="Enter platform name - e.g., deadly.coders"
                state={platformName}
                setState={setPlatformName}
              /> */}
              <TextInput
                title="Tenant Name (Non-User Facing)*"
                placeholder="tenantname.etgo.kazacos.ai"
                state={tenantName}
                setState={setTenantName}
              />
              <TextInput
                title="Support email*"
                placeholder="e.g., support@brand.com"
                state={supportEmail}
                setState={setSupportEmail}
              />
              <TextInput
                title="No Reply email*"
                placeholder="e.g., no-reply@brand.com"
                state={noReplyEmail}
                setState={setNoReplyEmail}
              />
              <TextInput
                title="No Reply email Host*"
                placeholder="e.g., smtp-mail.outlook.com"
                state={noReplyEmailHost}
                setState={setNoReplyEmailHost}
              />
              <TextInput
                title="No Reply email Password*"
                placeholder="Enter email password"
                state={noReplyEmailPwd}
                setState={setNoReplyEmailPwd}
              />
              <TextInput
                title="Licenses email*"
                placeholder="e.g., licenses@brand.com"
                state={licensesEmail}
                setState={setLicensesEmail}
              />
              <BinaryRadioButtons
                title="Customer type*"
                options={customerTypeOptions}
                selectedOption={customerType}
                setSelectedOption={setCustomerType}
              />
              <TextArea
                title="Brand Description*"
                placeholder="Provide a brief description about this brand. This will be shown on the login/sign-up screens."
                state={brandDescription}
                setState={setBrandDescription}
              />
              {/* <TextArea
                title="Feedback widget script"
                placeholder={`e.g., \r<script type="text/javascript">\r    (function(k,s) { s=document.createElement('script');s.module=true;s.async=true;   s.src="https://cdn.feedbucket.app/assets/feedbucket.js";  s.dataset.feedbucket=k;document.head.appendChild(s);\r    })('qKDo8WoAtP4FmkUMtgu7')\r</script>`}
                state={feedbackWidget}
                setState={setFeedbackWidget}
                textAreaClassName="w-full h-[221px] justify-start items-start gap-2 inline-flex"
              /> */}
              <TextInput
                title="Feedbucket Widget Key"
                placeholder={`e.g., aBCdefGHk12345lmnopQ`}
                state={feedbackWidget}
                setState={setFeedbackWidget}
              />
            </div>
          </div>
          <div className="w-full h-full rounded-3xl justify-start items-start gap-6 inline-flex flex-col">
            <div className="self-stretch h-fit flex-col justify-start items-start gap-12 flex">
              <div className="self-stretch text-neutral-700 text-[23px] font-semibold font-sans leading-7 select-none">
                Additional details
              </div>
            </div>
            <AddContact
              selectedContacts={selectedContacts}
              handleClickAddContacts={() => setShowEditContact(true)}
              setSelectedContacts={setSelectedContacts}
              setSelectedData={setSelectedDataContact}
              setShowEditContact={setShowEditContact}
            />
            <AddLocation
              locations={locations}
              setLocations={setLocations}
              setShowAddLocationPopUp={setShowAddLocationPopUp}
            />
          </div>
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7">
              Curriculum details
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
              <div className="justify-start text-neutral-500 text-xs font-normal font-sans uppercase leading-none tracking-wide">
                lesson Categories*
              </div>
              {lessonCategories && lessonCategories.length === 0 && (
                <AddListButton
                  icon={<MdMenuBook className="w-6 h-6 fill-neutral-500" />}
                  text="Select lesson categories"
                  onClickFunction={() => setShowAddLessonsPopUp(true)}
                />
              )}
              {lessonCategories && lessonCategories.length > 0 && (
                <div className="self-stretch flex flex-col justify-start items-start gap-5">
                  <div className="self-stretch inline-flex justify-start items-start gap-3 flex-wrap content-start">
                    {lessonCategories.map((lesson: string, index: number) => {
                      return (
                        <CategoryLessonPill
                          key={index}
                          lesson={lesson}
                          setLessonCategories={setLessonCategories}
                        />
                      );
                    })}
                  </div>
                  <button
                    className="group w-fit rounded justify-start items-center gap-2 flex cursor-pointer"
                    onClick={() => setShowAddLessonsPopUp(true)}
                  >
                    <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                    <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                      Add/Remove lessons
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7">
              AI Assistant
            </div>
            <div className="self-stretch inline-flex flex-col justify-start items-center gap-5">
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start text-neutral-500 text-base font-normal font-sans leading-tight">
                  Enable AI Assistant
                </div>
                <AnimatedToggle
                  state={enableAIAssistant}
                  handleClick={() => setEnableAIAssistant(!enableAIAssistant)}
                />
              </div>
              {enableAIAssistant && (
                <>
                  <InputBadgeImage
                    text="Upload AI Avatar image"
                    badgeImage={AIAvatar}
                    setBadgeImage={setAIAvatar}
                  />
                  <TextInput
                    title="AI Name*"
                    placeholder="Enter the name for your AI"
                    state={AIAssistantName}
                    setState={setAIAssistantName}
                  />
                  <TextInput
                    title="AI Greeting Intro*"
                    placeholder="Enter the greeting for your AI"
                    state={AIAssistantGreeting}
                    setState={setAIAssistantGreeting}
                  />
                  <TextArea
                    title="AI Greeting*"
                    placeholder="Tell us about how would you want this AI to introduce itself to the learner."
                    state={AIAssistantIntro}
                    setState={setAIAssistantIntro}
                  />
                  <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                    <div className="justify-start text-neutral-500 text-xs font-normal font-sans uppercase leading-none tracking-wide">
                      AI Prompts
                    </div>
                    {AIPrompts && AIPrompts.length === 0 && (
                      <AddListButton
                        icon={<MdAdd className="w-6 h-6 fill-neutral-500" />}
                        text="Add an AI prompt"
                        onClickFunction={() => setShowAddAIPromptPopUp(true)}
                      />
                    )}
                    {AIPrompts && AIPrompts.length > 0 && (
                      <div className="self-stretch flex flex-col justify-start items-start gap-5">
                        <div className="self-stretch inline-flex justify-start items-start gap-3 flex-wrap content-start">
                          {AIPrompts.sort(
                            // Sort by the primaries first
                            (a, b) => Number(b.primary) - Number(a.primary)
                          ).map(
                            (
                              prompt: { text: string; primary: boolean },
                              index: number
                            ) => {
                              return (
                                <AIPromptPill
                                  key={index}
                                  prompt={prompt}
                                  setPrompts={setAIPrompts}
                                />
                              );
                            }
                          )}
                        </div>
                        <button
                          className="group w-fit rounded justify-start items-center gap-2 flex cursor-pointer"
                          onClick={() => setShowAddAIPromptPopUp(true)}
                        >
                          <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                          <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                            Add another AI Prompt
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  {enableAIAssistant2 && (
                    <>
                      <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7 mt-7">
                        Second AI Assistant
                      </div>
                      <InputBadgeImage
                        text="Upload AI Avatar image"
                        badgeImage={AIAvatar2}
                        setBadgeImage={setAIAvatar2}
                      />
                      <TextInput
                        title="AI Name*"
                        placeholder="Enter the name for your AI"
                        state={AIAssistantName2}
                        setState={setAIAssistantName2}
                      />
                      <TextInput
                        title="AI Greeting Intro*"
                        placeholder="Enter the greeting for your AI"
                        state={AIAssistantGreeting2}
                        setState={setAIAssistantGreeting2}
                      />
                      <TextArea
                        title="AI Greeting*"
                        placeholder="Tell us about how would you want this AI to introduce itself to the learner."
                        state={AIAssistantIntro2}
                        setState={setAIAssistantIntro2}
                      />
                      <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                        <div className="justify-start text-neutral-500 text-xs font-normal font-sans uppercase leading-none tracking-wide">
                          AI Prompts
                        </div>
                        {AIPrompts2 && AIPrompts2.length === 0 && (
                          <AddListButton
                            icon={
                              <MdAdd className="w-6 h-6 fill-neutral-500" />
                            }
                            text="Add an AI prompt"
                            onClickFunction={() =>
                              setShowAddAIPromptPopUp2(true)
                            }
                          />
                        )}
                        {AIPrompts2 && AIPrompts2.length > 0 && (
                          <div className="self-stretch flex flex-col justify-start items-start gap-5">
                            <div className="self-stretch inline-flex justify-start items-start gap-3 flex-wrap content-start">
                              {AIPrompts2.sort(
                                // Sort by the primaries first
                                (a, b) => Number(b.primary) - Number(a.primary)
                              ).map(
                                (
                                  prompt: { text: string; primary: boolean },
                                  index: number
                                ) => {
                                  return (
                                    <AIPromptPill
                                      key={index}
                                      prompt={prompt}
                                      setPrompts={setAIPrompts2}
                                    />
                                  );
                                }
                              )}
                            </div>
                            <button
                              className="group w-fit rounded justify-start items-center gap-2 flex cursor-pointer"
                              onClick={() => setShowAddAIPromptPopUp2(true)}
                            >
                              <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                              <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                                Add another AI Prompt
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {!enableAIAssistant2 && (
                    <AddListButton
                      icon={
                        <MdOutlineGroupAdd className="w-6 h-6 fill-neutral-500" />
                      }
                      text="Add another AI assistant"
                      onClickFunction={() => setEnableAIAssistant2(true)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7">
              Brand terminologies & structure
            </div>
            <div className="justify-start text-neutral-500 text-xs font-normal font-sans leading-none">
              Select the appropriate option(s)
            </div>
            <BinaryRadioButtons
              title="Relevant Terminology for Groups of learners*"
              options={classesTerminologyOptions}
              selectedOption={classTerminology}
              setSelectedOption={setClassTerminology}
            />
            <BinaryRadioButtons
              title="Relevant Terminology for Learners*"
              options={studentsTerminologyOptions}
              selectedOption={studentsTerminology}
              setSelectedOption={setStudentsTerminology}
            />
            <BinaryRadioButtons
              title="Relevant Terminology for Schools/Organisations*"
              options={schoolTerminologyOptions}
              selectedOption={schoolTerminology}
              setSelectedOption={setSchoolTerminology}
            />
          </div>
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7">
              Data requirements
            </div>
            <div className="justify-start text-neutral-500 text-xs font-normal font-sans leading-none">
              Select the appropriate option(s)
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              <div className="justify-start text-neutral-500 text-xs font-normal font-sans uppercase leading-none tracking-wide">
                Student/Trainee data requirements*
              </div>
              <div className="w-fit flex flex-col justify-start items-start gap-3">
                {dataRequirementsFields.map(
                  (option: { text: string; value: string }, index: number) => {
                    return (
                      <CheckboxItem
                        key={index}
                        text={option.text}
                        selected={dataRequirements.includes(option.value)}
                        onClickFunction={handleClickDataRequirementOption(
                          option
                        )}
                      />
                    );
                  }
                )}
              </div>
            </div>
          </div>
          <div className="self-stretch inline-flex flex-col justify-start items-start gap-10">
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
              <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-snas leading-7">
                Custom Imagery
              </div>
              <InputHeaderImage
                text="Upload a login screen image"
                headerImage={loginScreen}
                setHeaderImage={setLoginScreen}
              />
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start text-neutral-500 text-base font-normal font-sans leading-tight">
                  Use custom imagery for dashboards
                </div>
                <AnimatedToggle
                  state={addCustomDashboardImagery}
                  handleClick={() =>
                    setAddCustomDashboardImagery(!addCustomDashboardImagery)
                  }
                />
              </div>
            </div>
            {addCustomDashboardImagery && (
              <>
                <InputBadgeImage
                  id="learning-journey-bg-upload"
                  text="Upload background image for learning journey"
                  badgeImage={bgImageLearningJourney}
                  setBadgeImage={setBgImageLearningJourney}
                />
                <InputBadgeImage
                  id="knowledge-points-icon-upload"
                  text="Upload icon image for knowledge points"
                  badgeImage={iconKnowledgePoints}
                  setBadgeImage={setIconKnowledgePoints}
                />
                <InputBadgeImage
                  id="knowledge-points-bg-upload"
                  text="Upload background image for knowledge points"
                  badgeImage={bgImageKnowledgePoints}
                  setBadgeImage={setBgImageKnowledgePoints}
                />
                <InputBadgeImage
                  id="my-badges-bg-upload"
                  text="Upload background image for my badges"
                  badgeImage={bgImageMyBadges}
                  setBadgeImage={setBgImageMyBadges}
                />
              </>
            )}
            {/* Show this toggle when AI assistan is selected */}
            {enableAIAssistant && (
              <>
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-start text-neutral-500 text-base font-normal font-sans leading-tight">
                    Use custom imagery for AI Assistant
                  </div>
                  <AnimatedToggle
                    state={addCustomAIAssistantImagery}
                    handleClick={() =>
                      setAddCustomAIAssistantImagery(
                        !addCustomAIAssistantImagery
                      )
                    }
                  />
                </div>
                {addCustomAIAssistantImagery && (
                  <>
                    <InputBadgeImage
                      text="Upload background image for AI assistant - Bottom left"
                      badgeImage={bgAIAssistantImage2}
                      setBadgeImage={setBgAIAssistantImage2}
                    />
                    <InputBadgeImage
                      text="Upload background image for AI assistant - Top right"
                      badgeImage={bgAIAssistantImage1}
                      setBadgeImage={setBgAIAssistantImage1}
                    />
                  </>
                )}
              </>
            )}
          </div>
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch justify-start text-neutral-700 text-2xl font-semibold font-sans leading-7">
              Brand Colors
            </div>
            <div className="self-stretch flex flex-col gap-5">
              <TextInput
                title="AI assistant message background colour"
                placeholder="Enter hex color code (e.g., #FFFFFF)"
                state={secondary50}
                setState={setSecondary50}
              />
              <TextInput
                title="'Dismiss' button background colour"
                placeholder="Enter hex color code"
                state={secondary100}
                setState={setSecondary100}
              />
              <TextInput
                title="Loading AI assistant message background colour"
                placeholder="Enter hex color code"
                state={secondary200}
                setState={setSecondary200}
              />
              {/* <TextInput
                title="Secondary 300"
                placeholder="Enter hex color code"
                state={secondary300}
                setState={setSecondary300}
              />
              <TextInput
                title="Secondary 400"
                placeholder="Enter hex color code"
                state={secondary400}
                setState={setSecondary400}
              /> */}
              <TextInput
                title="Loading dots, 'Done' button, and onDrag image field border colour"
                placeholder="Enter hex color code"
                state={secondary500}
                setState={setSecondary500}
              />
              <TextInput
                title="Main platform colour (buttons, bold text...)"
                placeholder="Enter hex color code"
                state={secondary600}
                setState={setSecondary600}
              />
              <TextInput
                title="Second main platform colour (on-hover, some text...)"
                placeholder="Enter hex color code"
                state={secondary700}
                setState={setSecondary700}
              />
              {/* <TextInput
                title="Secondary 800"
                placeholder="Enter hex color code"
                state={secondary800}
                setState={setSecondary800}
              />
              <TextInput
                title="Secondary 900"
                placeholder="Enter hex color code"
                state={secondary900}
                setState={setSecondary900}
              /> */}
            </div>
          </div>
          <div className="self-stretch inline-flex flex-col justify-start items-start gap-3">
            <div
              className={`self-stretch px-5 py-3 ${
                activateSaveButton
                  ? "bg-secondary-600 hover:bg-secondary-700 text-neutral-25 ease-in-out duration-150 cursor-pointer"
                  : "bg-Subtle text-neutral-400 cursor-not-allowed"
              } rounded inline-flex justify-center items-center`}
              onClick={handleSave}
            >
              <div className="justify-start text-lg font-semibold font-sans leading-snug">
                Save and deploy
              </div>
            </div>
            <div
              className={`self-stretch px-5 py-3 rounded border ${
                activateSaveButton
                  ? "border-secondary-600 text-secondary-600 hover:bg-neutral-25 ease-in-out duration-150 cursor-pointer"
                  : "border-Subtle text-Subtle cursor-not-allowed"
              } inline-flex justify-center items-center`}
              onClick={handleSave}
            >
              <div className="justify-start text-lg font-semibold font-sans leading-snug">
                Save
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {/* Edit contacts pop-up */}
          {showEditContact && (
            <></>
            // <CreateEditContact
            //   role="master admin"
            //   fromCreateNew={!selectedData}
            //   confirmButtonText="Save contact"
            //   contactToEdit={selectedDataContact}
            //   contacts={selectedContacts}
            //   showPrimaryOption={false}
            //   setContacts={setSelectedContacts}
            //   handlePressBack={() => setShowEditContact(false)}
            //   afterCreateEditContact={() => setShowEditContact(false)}
            // />
          )}
          {/* Add location pop-up */}
          {showAddLocationPopUp && (
            <AddLocationPanel
              setLocations={setLocations}
              setShowPopUp={setShowAddLocationPopUp}
            />
          )}
          {/* Pop up for selecting lessons categories */}
          {showAddLessonsPopUp && (
            <SelectLessonCategoriesPanel
              selectedData={lessonCategories}
              setShowPopUp={setShowAddLessonsPopUp}
              setSelectedData={setLessonCategories}
            />
          )}
          {showAddAIPromptPopUp && (
            <AddAIPromptPanel
              currentPrompts={AIPrompts}
              limitPrompts={AI_PROMPTS_LIMIT}
              setPrompts={setAIPrompts}
              setShowPopUp={setShowAddAIPromptPopUp}
            />
          )}
          {showAddAIPromptPopUp2 && (
            <AddAIPromptPanel
              currentPrompts={AIPrompts2}
              limitPrompts={AI_PROMPTS_LIMIT}
              setPrompts={setAIPrompts2}
              setShowPopUp={setShowAddAIPromptPopUp2}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default AddNewBrand;
